import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LOGO_URL, APP_NAME } from '../lib/constants';
import {
  Shield, Users, FileText, BarChart3, LogOut, Clock,
  Loader2, RefreshCw, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { getAdminToken, clearAdminToken } from '../components/admin/adminUtils';
import { OverviewTab } from '../components/admin/OverviewTab';
import { UsersTab } from '../components/admin/UsersTab';
import { AssessmentsTab } from '../components/admin/AssessmentsTab';
import { InvitationsTab } from '../components/admin/InvitationsTab';
import { UserDetailModal } from '../components/admin/UserDetailModal';
import { AssessmentDetailModal } from '../components/admin/AssessmentDetailModal';
import { GuestAssessmentsTab } from '../components/admin/GuestAssessmentsTab';
import { ActionPlanEditorModal } from '../components/admin/ActionPlanEditorModal';
import { InPersonAssessmentModal } from '../components/admin/InPersonAssessmentModal';
import { DeleteUserDialog } from '../components/admin/DeleteUserDialog';

const TABS = ['overview', 'users', 'assessments', 'invitations', 'guests'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [userAssessments, setUserAssessments] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Follow-up state
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Delete user state
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // Invitation state
  const [invitations, setInvitations] = useState([]);
  const [guestAssessments, setGuestAssessments] = useState([]);
  const [showNewInvitationForm, setShowNewInvitationForm] = useState(false);
  const [newInvitation, setNewInvitation] = useState({
    email: '', name: '', company_name: '', jurisdiction: 'jamaica',
    industry: '', phone: '', internal_notes: '',
    status: 'prospect', expires_in_days: 14,
  });
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(null);
  const [selectedGuestAssessment, setSelectedGuestAssessment] = useState(null);
  const [downloadingWord, setDownloadingWord] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(null);
  const [inPersonModalOpen, setInPersonModalOpen] = useState(false);

  const adminToken = getAdminToken();
  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, assessmentsRes, invitationsRes, guestAssessmentsRes] = await Promise.all([
        api.get('/admin/analytics', { headers }),
        api.get('/admin/users', { headers }),
        api.get('/admin/assessments', { headers }),
        api.get('/admin/invitations', { headers }),
        api.get('/admin/guest-assessments', { headers }),
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users);
      setAssessments(assessmentsRes.data.assessments);
      setInvitations(invitationsRes.data.invitations || []);
      setGuestAssessments(guestAssessmentsRes.data.assessments || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearAdminToken();
        navigate('/admin');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, navigate]);

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [adminToken, navigate, fetchData]);

  // ==================== USER HANDLERS ====================

  const handleViewUser = async (user) => {
    setLoadingDetail(true);
    setSelectedUser(user);
    try {
      const response = await api.get(`/admin/users/${user.id}`, { headers });
      setSelectedUser(response.data.user);
      setUserAssessments(response.data.assessments);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await api.put(`/admin/users/${userId}/followup`, { status: newStatus }, { headers });
      setSelectedUser(response.data.user);
      setUsers((prev) => prev.map((u) => (u.id === userId ? response.data.user : u)));
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async (userId) => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const response = await api.put(`/admin/users/${userId}/followup`, { notes: newNote }, { headers });
      setSelectedUser(response.data.user);
      setUsers((prev) => prev.map((u) => (u.id === userId ? response.data.user : u)));
      setNewNote('');
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (userId, noteIndex) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/followup/notes/${noteIndex}`, { headers });
      setSelectedUser(response.data.user);
      setUsers((prev) => prev.map((u) => (u.id === userId ? response.data.user : u)));
      toast.success('Note deleted');
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const handleDeleteUserClick = (user) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      const response = await api.delete(`/admin/users/${userToDelete.id}`, { headers });
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setAssessments((prev) => prev.filter((a) => a.user_id !== userToDelete.id));
      if (selectedUser?.id === userToDelete.id) {
        setSelectedUser(null);
      }
      toast.success(`User deleted (${response.data.assessments_deleted} assessments removed)`);
    } catch (err) {
      toast.error('Failed to delete user');
    } finally {
      setDeletingUser(false);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
    }
  };

  // ==================== ASSESSMENT HANDLERS ====================

  const handleViewAssessment = async (assessment) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/admin/assessments/${assessment.id}`, { headers });
      setSelectedAssessment({ ...response.data.assessment, user: response.data.user });
    } catch (err) {
      toast.error('Failed to load assessment details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownloadPDF = async (assessmentId, companyName, jurisdiction) => {
    try {
      const response = await api.get(`/admin/assessments/${assessmentId}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${companyName}_${jurisdiction}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  // ==================== INVITATION HANDLERS ====================

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    if (!newInvitation.email || !newInvitation.jurisdiction) {
      toast.error('Email and jurisdiction are required');
      return;
    }
    setCreatingInvitation(true);
    try {
      const response = await api.post('/admin/invitations', newInvitation, { headers });
      setInvitations((prev) => [response.data, ...prev]);
      setShowNewInvitationForm(false);
      setNewInvitation({
        email: '', name: '', company_name: '', jurisdiction: 'jamaica',
        industry: '', phone: '', internal_notes: '',
        status: 'prospect', expires_in_days: 14,
      });
      await navigator.clipboard.writeText(response.data.invitation_link);
      toast.success('Invitation created! Link copied to clipboard.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setCreatingInvitation(false);
    }
  };

  const handleCopyInvitationLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleSendInvitationEmail = async (invitationId) => {
    setSendingInvitation(invitationId);
    try {
      const response = await api.post(`/admin/invitations/${invitationId}/send`, {}, { headers });
      if (response.data.email_sent) {
        toast.success('Invitation email sent!');
      } else {
        toast.info('Email not configured. Link copied to clipboard.');
        await navigator.clipboard.writeText(response.data.invitation_link);
      }
    } catch (err) {
      toast.error('Failed to send invitation');
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this invitation? If the guest has already completed it, their submission will also be deleted.')) return;
    try {
      await api.delete(`/admin/invitations/${invitationId}`, { headers });
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
      toast.success('Invitation deleted');
    } catch (err) {
      toast.error('Failed to delete invitation');
    }
  };

  const handleViewGuestAssessment = async (assessment) => {
    setLoadingDetail(true);
    try {
      const response = await api.get(`/admin/guest-assessments/${assessment.id}`, { headers });
      setSelectedGuestAssessment(response.data);
    } catch (err) {
      toast.error('Failed to load guest assessment details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const downloadFile = async (url, filename, blob) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadGuestWord = async (assessment) => {
    setDownloadingWord(assessment.id);
    try {
      const response = await api.get(`/admin/guest-assessments/${assessment.id}/word`, {
        headers, responseType: 'blob',
      });
      const company = (assessment.guest_info?.company_name || 'Guest').replace(/\s+/g, '_');
      const jurisdiction = (assessment.jurisdiction || 'Report').replace(/\s+/g, '_');
      await downloadFile(null, `${company}_${jurisdiction}_Compliance_Report.docx`, new Blob([response.data]));
      toast.success('Word report downloaded');
    } catch (err) {
      toast.error('Failed to download Word report');
    } finally {
      setDownloadingWord(null);
    }
  };

  const handleDownloadGuestExcel = async (assessment) => {
    setDownloadingExcel(assessment.id);
    try {
      const response = await api.get(`/admin/guest-assessments/${assessment.id}/excel`, {
        headers, responseType: 'blob',
      });
      const company = (assessment.guest_info?.company_name || 'Guest').replace(/\s+/g, '_');
      const jurisdiction = (assessment.jurisdiction || 'Responses').replace(/\s+/g, '_');
      await downloadFile(null, `${company}_${jurisdiction}_Responses.xlsx`, new Blob([response.data]));
      toast.success('Excel Q&A downloaded');
    } catch (err) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(null);
    }
  };

  // ==================== TAB / RENDER ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt={APP_NAME} className="h-8" />
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#3B82F6]" />
              <span className="font-bold text-white font-['Manrope']">Admin Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Users" value={analytics?.total_users} Icon={Users} color="#3B82F6" />
          <StatCard label="Completed Assessments" value={analytics?.completed_assessments} Icon={FileText} color="#10B981" />
          <StatCard label="In Progress" value={analytics?.in_progress_assessments} Icon={Clock} color="#F59E0B" />
          <StatCard label="Total Assessments" value={analytics?.total_assessments} Icon={BarChart3} color="#8B5CF6" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              data-testid={`admin-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab
                ? 'bg-[#3B82F6] text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'}
            >
              {tab === 'invitations' ? (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invitations
                  {invitations.filter((i) => i.status === 'completed').length > 0 && (
                    <Badge className="ml-2 bg-[#10B981] text-white text-xs">
                      {invitations.filter((i) => i.status === 'completed').length}
                    </Badge>
                  )}
                </>
              ) : tab === 'guests' ? (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Guest Results
                  {guestAssessments.filter((a) => a.status === 'completed').length > 0 && (
                    <Badge className="ml-2 bg-[#10B981] text-white text-xs">
                      {guestAssessments.filter((a) => a.status === 'completed').length}
                    </Badge>
                  )}
                </>
              ) : (
                tab.charAt(0).toUpperCase() + tab.slice(1)
              )}
            </Button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewTab analytics={analytics} />}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            onView={handleViewUser}
            onDeleteClick={handleDeleteUserClick}
          />
        )}
        {activeTab === 'assessments' && (
          <AssessmentsTab
            assessments={assessments}
            onView={handleViewAssessment}
            onDownload={handleDownloadPDF}
          />
        )}
        {activeTab === 'invitations' && (
          <InvitationsTab
            invitations={invitations}
            guestAssessments={guestAssessments}
            showNewInvitationForm={showNewInvitationForm}
            setShowNewInvitationForm={setShowNewInvitationForm}
            newInvitation={newInvitation}
            setNewInvitation={setNewInvitation}
            creatingInvitation={creatingInvitation}
            sendingInvitation={sendingInvitation}
            onCreate={handleCreateInvitation}
            onCopyLink={handleCopyInvitationLink}
            onSendEmail={handleSendInvitationEmail}
            onDelete={handleDeleteInvitation}
            onViewGuest={handleViewGuestAssessment}
            onStartInPerson={() => setInPersonModalOpen(true)}
          />
        )}
        {activeTab === 'guests' && (
          <GuestAssessmentsTab
            guestAssessments={guestAssessments}
            onView={handleViewGuestAssessment}
            onDownloadWord={handleDownloadGuestWord}
            onDownloadExcel={handleDownloadGuestExcel}
            downloadingWord={downloadingWord}
            downloadingExcel={downloadingExcel}
          />
        )}
      </div>

      <ActionPlanEditorModal
        data={selectedGuestAssessment}
        loading={loadingDetail}
        onClose={() => setSelectedGuestAssessment(null)}
        onDownloadWord={handleDownloadGuestWord}
        onDownloadExcel={handleDownloadGuestExcel}
        downloadingWord={downloadingWord}
        downloadingExcel={downloadingExcel}
        headers={headers}
        onSaved={async () => {
          // Refetch this guest assessment + the list to reflect edits
          if (selectedGuestAssessment?.assessment?.id) {
            try {
              const r = await api.get(`/admin/guest-assessments/${selectedGuestAssessment.assessment.id}`, { headers });
              setSelectedGuestAssessment(r.data);
            } catch (e) { /* noop */ }
          }
          // Also refresh the list so badge counts and timestamps update
          try {
            const r = await api.get('/admin/guest-assessments', { headers });
            setGuestAssessments(r.data.assessments || []);
          } catch (e) { /* noop */ }
        }}
      />

      <UserDetailModal
        user={selectedUser}
        loading={loadingDetail}
        assessments={userAssessments}
        newNote={newNote}
        setNewNote={setNewNote}
        savingNote={savingNote}
        updatingStatus={updatingStatus}
        onClose={() => setSelectedUser(null)}
        onUpdateStatus={handleUpdateStatus}
        onAddNote={handleAddNote}
        onDeleteNote={handleDeleteNote}
        onSwitchToAssessment={(assessment) => {
          setSelectedUser(null);
          handleViewAssessment(assessment);
        }}
        onDownloadPDF={handleDownloadPDF}
      />

      <AssessmentDetailModal
        assessment={selectedAssessment}
        loading={loadingDetail}
        onClose={() => setSelectedAssessment(null)}
        onDownloadPDF={handleDownloadPDF}
      />

      <DeleteUserDialog
        open={deleteUserDialogOpen}
        onOpenChange={setDeleteUserDialogOpen}
        user={userToDelete}
        deleting={deletingUser}
        onConfirm={handleDeleteUserConfirm}
      />

      <InPersonAssessmentModal
        open={inPersonModalOpen}
        onClose={() => setInPersonModalOpen(false)}
        headers={headers}
        existingClients={invitations}
        onCreated={(invitation) => {
          setInvitations((prev) => [invitation, ...prev]);
        }}
      />
    </div>
  );
};

const StatCard = ({ label, value, Icon, color }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{label}</p>
          <p className="text-3xl font-bold text-white">{value || 0}</p>
        </div>
        <Icon className="h-10 w-10" style={{ color }} />
      </div>
    </CardContent>
  </Card>
);

export default AdminDashboard;
